import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LayoutSeo from './LayoutSeo';
import { slugifyForUrl } from '../lib/areaSlug';
import {
  getListingAreaGroupKey,
  getListingAreaGroupLabel,
} from '../lib/locationRegions';
import { usePublishedListings } from '../hooks/usePublishedListings';

const PHONE_DISPLAY = '+27 82 786 0666';
const PHONE_E164 = '27827860666';
const WHATSAPP_URL = `https://wa.me/${PHONE_E164}`;

export default function Layout() {
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isAreasOpen, setIsAreasOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const { data: publishedListings } = usePublishedListings();

  const areaCities = useMemo(() => {
    const src = publishedListings || [];
    const byCity = new Map<string, { label: string; count: number }>();
    for (const l of src) {
      const label = getListingAreaGroupLabel(l);
      const key = getListingAreaGroupKey(l);
      if (!key) continue;
      const prev = byCity.get(key);
      byCity.set(key, { label, count: (prev?.count || 0) + 1 });
    }
    return [...byCity.values()].sort((a, b) => b.count - a.count).slice(0, 12);
  }, [publishedListings]);

  return (
    <div className="min-h-screen flex flex-col">
      <LayoutSeo />
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link
              to="/"
              className="flex items-center space-x-3 text-xl md:text-2xl font-bold text-[#32373c] font-heading hover:text-[#2a2e33] transition-colors"
            >
              <img
                src="/brand/Company Logo/leisure-breaks-logo-web-version-240x160.png"
                alt="Leisure Breaks"
                className="h-8 md:h-10 w-auto"
                loading="lazy"
              />
            </Link>
            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#000000] hover:text-[#32373c] text-xs md:text-sm font-medium transition-colors"
              >
                <i className="uil uil-whatsapp text-base mr-1.5 text-green-600"></i>
                {PHONE_DISPLAY}
              </a>
              <Link
                to="/find-property"
                className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
              >
                <i className="uil uil-map-marker text-base mr-1.5"></i>
                Find Property
              </Link>
              <div
                className="relative"
                onMouseEnter={() => setIsAreasOpen(true)}
                onMouseLeave={() => setIsAreasOpen(false)}
              >
                <button
                  type="button"
                  className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
                  aria-haspopup="menu"
                  aria-expanded={isAreasOpen}
                  onClick={() => setIsAreasOpen((v) => !v)}
                >
                  <i className="uil uil-location-point text-base mr-1.5" aria-hidden />
                  Browse by area
                  <i className="uil uil-angle-down text-base ml-1" aria-hidden />
                </button>
                {isAreasOpen && (
                  <div className="absolute top-full left-0 pt-2 w-64 z-50">
                    <div
                      role="menu"
                      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                    >
                    {areaCities.length > 0 ? (
                      <div className="py-2">
                        {areaCities.map((c) => (
                          <Link
                            key={c.label}
                            to={`/areas/${slugifyForUrl(c.label)}`}
                            className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
                            onClick={() => setIsAreasOpen(false)}
                            role="menuitem"
                          >
                            {c.label}{' '}
                            <span className="text-gray-400">({c.count})</span>
                          </Link>
                        ))}
                        <div className="border-t border-gray-100 mt-1" />
                        <Link
                          to="/search"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsAreasOpen(false)}
                          role="menuitem"
                        >
                          View all properties →
                        </Link>
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-600">
                        No areas yet.
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </div>
              <Link
                to="/search"
                className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
              >
                <i className="uil uil-search text-base mr-1.5"></i>
                Search
              </Link>
              {currentUser ? (
                <>
                  {userData && ['admin', 'manager', 'host'].includes(userData.role) && (
                    <Link
                      to="/admin/dashboard"
                      className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
                    >
                      <i className="uil uil-setting text-base mr-1.5"></i>
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/bookings"
                    className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
                  >
                    <i className="uil uil-calendar-alt text-base mr-1.5"></i>
                    Bookings
                  </Link>
                  <Link
                    to="/account"
                    className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
                  >
                    <i className="uil uil-user text-base mr-1.5"></i>
                    Account
                  </Link>
                  <span className="text-[#000000] text-sm">{userData?.name}</span>
                  <button onClick={handleLogout} className="btn btn-secondary">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-[#000000] hover:text-[#32373c] font-medium flex items-center transition-colors text-sm"
                  >
                    <i className="uil uil-signin text-base mr-1.5"></i>
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-primary flex items-center text-sm py-2 px-4"
                  >
                    <i className="uil uil-user-plus text-base mr-1.5"></i>
                    Sign Up
                  </Link>
                </>
              )}
            </div>
            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32373c]"
              aria-label="Toggle navigation"
              onClick={() => setIsMobileNavOpen((open) => !open)}
            >
              <i className={`uil ${isMobileNavOpen ? 'uil-multiply' : 'uil-bars'} text-2xl`}></i>
            </button>
          </div>
        </nav>
        {/* Mobile nav panel */}
        {isMobileNavOpen && (
          <div className="md:hidden bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 pb-4 space-y-2">
              <Link
                to="/find-property"
                className="flex items-center py-2 text-sm font-medium text-gray-800"
                onClick={() => setIsMobileNavOpen(false)}
              >
                <i className="uil uil-map-marker text-base mr-2"></i>
                Find Property
              </Link>
              <div className="pt-1">
                <div className="flex items-center py-2 text-sm font-semibold text-gray-800">
                  <i className="uil uil-location-point text-base mr-2" aria-hidden />
                  Browse by area
                </div>
                <div className="pl-7 space-y-1">
                  {areaCities.length > 0 ? (
                    areaCities.map((c) => (
                      <Link
                        key={c.label}
                        to={`/areas/${slugifyForUrl(c.label)}`}
                        className="block py-1.5 text-sm text-gray-800"
                        onClick={() => setIsMobileNavOpen(false)}
                      >
                        {c.label}{' '}
                        <span className="text-gray-400">({c.count})</span>
                      </Link>
                    ))
                  ) : (
                    <div className="py-1.5 text-sm text-gray-500">No areas yet.</div>
                  )}
                </div>
              </div>
              <Link
                to="/search"
                className="flex items-center py-2 text-sm font-medium text-gray-800"
                onClick={() => setIsMobileNavOpen(false)}
              >
                <i className="uil uil-search text-base mr-2"></i>
                Search
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center py-2 text-sm font-medium text-gray-800"
                onClick={() => setIsMobileNavOpen(false)}
              >
                <i className="uil uil-whatsapp text-base mr-2 text-green-600"></i>
                {PHONE_DISPLAY}
              </a>
              {currentUser ? (
                <>
                  {userData && ['admin', 'manager', 'host'].includes(userData.role) && (
                    <Link
                      to="/admin/dashboard"
                      className="flex items-center py-2 text-sm font-medium text-gray-800"
                      onClick={() => setIsMobileNavOpen(false)}
                    >
                      <i className="uil uil-setting text-base mr-2"></i>
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/bookings"
                    className="flex items-center py-2 text-sm font-medium text-gray-800"
                    onClick={() => setIsMobileNavOpen(false)}
                  >
                    <i className="uil uil-calendar-alt text-base mr-2"></i>
                    Bookings
                  </Link>
                  <Link
                    to="/account"
                    className="flex items-center py-2 text-sm font-medium text-gray-800"
                    onClick={() => setIsMobileNavOpen(false)}
                  >
                    <i className="uil uil-user text-base mr-2"></i>
                    Account
                  </Link>
                  <button
                    onClick={() => {
                      setIsMobileNavOpen(false);
                      handleLogout();
                    }}
                    className="w-full text-left flex items-center py-2 text-sm font-medium text-gray-800"
                  >
                    <i className="uil uil-signout text-base mr-2"></i>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center py-2 text-sm font-medium text-gray-800"
                    onClick={() => setIsMobileNavOpen(false)}
                  >
                    <i className="uil uil-signin text-base mr-2"></i>
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center py-2 text-sm font-medium text-gray-800"
                    onClick={() => setIsMobileNavOpen(false)}
                  >
                    <i className="uil uil-user-plus text-base mr-2"></i>
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="bg-[#32373c] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/brand/Company Logo/leisure-breaks-logo-web-version-240x160.png"
                  alt="Leisure Breaks"
                  className="h-8 w-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-gray-400">
                Your trusted partner for premium vacation rentals in South Africa.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/find-property" className="hover:text-white">
                    Find Property on Map
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="hover:text-white">
                    Search Properties
                  </Link>
                </li>
                <li>
                  <Link to="/account" className="hover:text-white">
                    My Account
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <p className="text-gray-400">
                Email: info@leisurebreaks.co.za
                <br />
                Phone:{' '}
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white underline-offset-2">
                  {PHONE_DISPLAY}
                </a>
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Leisure Breaks. All rights reserved.</p>
            <div className="mt-4 flex justify-center items-center gap-1 text-sm">
              <span>Powered by</span>
              <a
                href="https://thinkincode.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-white hover:underline focus:outline-none focus:ring-2 focus:ring-white/40 rounded-sm"
              >
                thinkinCode(☁️);
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp chat button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-4 bottom-4 z-40 flex items-center justify-center rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg w-12 h-12 md:w-14 md:h-14 transition-transform hover:scale-105"
        aria-label="Chat on WhatsApp"
      >
        <i className="uil uil-whatsapp text-2xl md:text-3xl"></i>
      </a>
    </div>
  );
}
